import { computed, reactive } from 'vue'

/**
 * Suivi des appels en cours.
 *
 * Toutes les mutations passent par `callFunction` : c'est donc le seul endroit où savoir
 * qu'une opération est en vol. Ce module tient le compte de ces appels, et l'indicateur
 * global s'y abonne. Aucun écran n'a à gérer lui-même son voyant d'attente, et aucune
 * action ne peut être oubliée — y compris celles ajoutées plus tard.
 *
 * Deux seuils, parce qu'un voyant mal dosé est pire que pas de voyant :
 *  - en dessous de `QUICK_MS`, rien ne s'affiche : un éclair de spinner sur un appel de
 *    200 ms donne l'impression que l'application clignote ;
 *  - au delà de `LONG_MS`, on ne se contente plus d'un trait de progression : on nomme
 *    l'opération et on affiche le temps écoulé. C'est ce qui distingue « ça tourne » de
 *    « ça a planté », la question que se posait l'animateur devant un écran figé.
 */

/** En dessous de ce délai, l'appel se termine avant qu'on ait eu besoin de prévenir. */
const QUICK_MS = 400

/** Au delà, l'attente mérite une explication et un compteur. */
const LONG_MS = 1500

/** Rythme de rafraîchissement du temps écoulé. */
const TICK_MS = 250

/**
 * Opérations dont on sait qu'elles durent : on explique dès le départ, sans attendre
 * `LONG_MS`. Inutile de laisser l'animateur deux secondes devant un écran muet quand on
 * sait déjà que l'appel prendra le temps qu'il prendra.
 */
const LONG_OPERATIONS = {
  exportWorldData: 'Préparation de la sauvegarde',
  importWorldData: 'Restauration de la sauvegarde',
  resetWorldGameData: 'Réinitialisation de la partie',
  migrateLegacyWorld: 'Mise à jour des données de la session',
  clearPeriodResults: 'Suppression des résultats',
  copyStrategyToAllTeams: 'Recopie de la stratégie sur toutes les équipes',
  applyStrategySupplierToAllTeams: 'Application du fournisseur à toutes les équipes',
  requestNewContractsForAllTeams: 'Nouveaux contrats pour toutes les équipes',
  requestNewStaffForAllTeams: 'Nouveaux candidats pour toutes les équipes',
  requestNewContractsForOffer: 'Nouveaux contrats pour cette offre',
  generateStrategyContractPool: 'Génération des contrats',
  generateStrategyStaffPool: 'Génération des candidats',
  initializeTeamStates: 'Initialisation des équipes',
  updateStateScores: 'Mise à jour des scores',
}

/**
 * Opérations qui affichent déjà leur propre attente, ou qui n'ont rien à afficher.
 *
 * `runPeriodSimulation` a son écran de progression détaillé dans la vue des résultats :
 * un second voyant par dessus ne dirait rien de plus. `ensurePeriodBootstrap` est déclenchée
 * automatiquement à chaque abonnement, sans que personne ne l'ait demandée — la signaler
 * ferait clignoter l'application à l'ouverture de chaque écran.
 */
const SILENT_OPERATIONS = new Set([
  'runPeriodSimulation',
  'ensurePeriodBootstrap',
])

/** Libellé d'une opération, déduit de son verbe quand elle n'est pas nommée explicitement. */
export function labelOf(name) {
  if (LONG_OPERATIONS[name]) return LONG_OPERATIONS[name]
  if (typeof name !== 'string') return 'Enregistrement'
  if (name.startsWith('create')) return 'Création'
  if (name.startsWith('delete') || name.startsWith('remove') || name.startsWith('cancel')) return 'Suppression'
  return 'Enregistrement'
}

const state = reactive({
  /** Appels en vol : { id, name, label, startedAt }. */
  calls: [],
  /** Horloge, rafraîchie tant qu'un appel est en cours. */
  now: Date.now(),
})

let nextId = 1
let ticker = null

function startTicker() {
  if (ticker) return
  state.now = Date.now()
  ticker = setInterval(() => { state.now = Date.now() }, TICK_MS)
}

function stopTicker() {
  if (!ticker) return
  clearInterval(ticker)
  ticker = null
}

/**
 * Signale le début d'un appel.
 * @returns {number|null} jeton à rendre à `endRequest`, ou `null` si l'appel est silencieux
 */
export function beginRequest(name) {
  if (SILENT_OPERATIONS.has(name)) return null

  const id = nextId++
  state.calls.push({ id, name, label: labelOf(name), startedAt: Date.now() })
  startTicker()
  return id
}

/** Signale la fin d'un appel, qu'il ait réussi ou échoué. */
export function endRequest(id) {
  if (id === null || id === undefined) return

  const index = state.calls.findIndex(call => call.id === id)
  if (index !== -1) state.calls.splice(index, 1)
  if (!state.calls.length) stopTicker()
}

/** Appel le plus ancien : c'est lui qui fait attendre, et donc lui qu'on nomme. */
const oldest = computed(() => {
  if (!state.calls.length) return null
  return state.calls.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b))
})

/** Durée d'attente en cours, en millisecondes. */
export const elapsed = computed(() => (oldest.value ? Math.max(0, state.now - oldest.value.startedAt) : 0))

/** Un appel est en vol. */
export const busy = computed(() => state.calls.length > 0)

/** L'attente est assez longue pour être signalée. */
export const visible = computed(() => busy.value && elapsed.value >= QUICK_MS)

/** L'attente mérite d'être nommée et chronométrée. */
export const detailed = computed(() => {
  if (!busy.value) return false
  return elapsed.value >= LONG_MS || state.calls.some(call => LONG_OPERATIONS[call.name])
})

/** Ce qu'on annonce à l'écran. */
export const label = computed(() => (oldest.value ? oldest.value.label : ''))

/** Nombre d'opérations simultanées, pour le cas des actions groupées. */
export const count = computed(() => state.calls.length)

export default { beginRequest, endRequest, labelOf, busy, visible, detailed, label, elapsed, count }
